/*
 * Copyright (c) 2020.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Nikita Tropin <ntropin@yandex.ru>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {Injectable, OnDestroy} from '@angular/core';
import {merge, MonoTypeOperatorFunction, Observable, Subject} from 'rxjs';
import {SubtaskLinkAction} from './../subtask-links-issue/subtask-link.action';
import {exhaustMap, filter, map, publishReplay, refCount, scan, startWith, tap} from 'rxjs/operators';
import {SubtaskLinkState} from '../subtask-links-issue/subtask-link.state';
import {TaskLinkService} from './task-link.service';
import {CrmIssueElastic} from 'compob2b-libs';

const defaultState: SubtaskLinkState = {
  items: [],
  loading: false
};

@Injectable({
  providedIn: 'root'
})
export class TaskLinkStore implements OnDestroy {

  actions$: Subject<SubtaskLinkAction> = new Subject<SubtaskLinkAction>();

  totalSpent = 0;
  totalEstimate = 0;

  load$: Observable<SubtaskLinkAction> =
    this.actions$.pipe(
      ofType('load'),
      exhaustMap((action: SubtaskLinkAction) => this.service.load(action.payload)),
      map(payload => ({
        type: 'load success', payload
      }))
    );

  store$: Observable<SubtaskLinkAction> =
    this.actions$.pipe(
      ofType('store'),
      exhaustMap((action: SubtaskLinkAction) => this.service.store(action.payload)),
      map(payload => ({
        type: 'store success', payload
      }))
    );

  delete$: Observable<SubtaskLinkAction> =
    this.actions$.pipe(
      ofType('delete'),
      exhaustMap((action: SubtaskLinkAction) => this.service.delete(action.payload)),
      map(payload => ({
        type: 'delete success', payload
      }))
    );

  /**
   * Dispatcher
   */
  dispatcher$: Observable<SubtaskLinkAction> =
    merge(this.actions$, this.load$, this.store$, this.delete$);

  /**
   * State Reducer
   */
  state$: Observable<SubtaskLinkState | SubtaskLinkAction> =
    this.dispatcher$.pipe(
      //@ts-ignore
      startWith(defaultState),
      //@ts-ignore
      scan((state: SubtaskLinkState, action: SubtaskLinkAction): SubtaskLinkState => {
        switch (action.type) {
          case 'load':
          case 'store':
          case 'delete':
            return {...state, loading: true};
          case 'delete success':
            const index = state.items.findIndex(item => item.id === action.payload);
            if (index !== -1) {
              state.items.splice(index, 1);
            }

            return {...state, loading: false};
          case 'load success':
            return {...state, items: action.payload, loading: false};
          case 'store success':
            const items = [];
            for (const item of action.payload) {
              if (state.items.findIndex(o => o.id === item.id) === -1) {
                items.push(item);
              }
            }

            return {
              ...state,
              items: [...state.items, ...items],
              loading: false
            };
          default:
            return state;
        }
      }),
      publishReplay(1),
      refCount()
    );

  /**
   * Selectors
   */
  items$: Observable<CrmIssueElastic[]> =
    this.state$.pipe(
      //@ts-ignore
      map((state: SubtaskLinkState) => {
        this.totalSpent = 0;
        this.totalEstimate = 0;
        return state.items;
      }),
      tap(issues => {
        issues.forEach(issue => {
          this.totalSpent += issue.spent;
          this.totalEstimate += issue.estimate;
        });
      }));
  count$: Observable<number> =
    this.items$.pipe(map((items: CrmIssueElastic[]) => items.length));
  loading$: Observable<boolean> =
    //@ts-ignore
    this.state$.pipe(map((state: SubtaskLinkState) => state.loading));

  constructor(private service: TaskLinkService) {
  }

  ngOnDestroy() {
    this.actions$.complete();
  }

  /**
   * Actions
   */

  load(id: number) {
    this.dispatch({type: 'load', payload: id});
  }

  store(id: number, links: CrmIssueElastic[]) {
    this.dispatch({type: 'store', payload: {id, links}});
  }

  delete(id: number, linkId: number) {
    this.dispatch({type: 'delete', payload: {id, linkId}});
  }

  private dispatch(action: SubtaskLinkAction): void {
    this.actions$.next(action);
  }
}

function ofType<T extends SubtaskLinkAction>(type: string): MonoTypeOperatorFunction<T> {
  return filter((_) => type === _.type);
}
